"use client";

import { z } from "zod";
import parsePhoneNumberFromString from "libphonenumber-js";
import { IZapierForm } from "@/sanity/globals/ZapierForm";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shadcn/ui/form";
import { Input } from "@/shadcn/ui/input";
import { Button } from "@/shadcn/ui/button";
import { CornerButton } from "@/shadcn/ui/corner-button";
import { stegaClean } from "@sanity/client/stega";
import { submitFormDataToZapier } from "@/helpers/zapier";
import { usePostHog } from "posthog-js/react";
import { sendGTMEvent } from "@next/third-parties/google";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import Loader from "@/components/global/Loader";
import { useAffiliateWebhook } from "@/components/global/AffiliateWebhookProvider";

export default function ZapierForm({
  formData,
  theme = "default"
}: {
  formData: IZapierForm;
  theme?: "default" | "new";
}) {
  const isNewTheme = theme === "new";
  const postHog = usePostHog();
  const [loading, setLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const router = useRouter();
  const forwardAffiliateToWebhook = useAffiliateWebhook();

  const cleanFormData = useMemo(() => {
    return stegaClean(formData);
  }, [formData]);

  const formFields = useMemo(() => {
    return cleanFormData?.fields?.reduce((obj, item) => {
      return {
        ...obj,
        [item.name]:
          item.type === "text" || item.type === "textarea"
            ? item.required
              ? z.string().min(1, { message: "Erforderlich" })
              : z.string()
            : item.type === "email"
              ? item.required
                ? z.string().min(1, { message: "Erforderlich" }).email({ message: "Ungültige E-Mail" })
                : z.string().email({ message: "Ungültige E-Mail" }).optional()
              : item.type === "phone"
                ? z.string().transform((arg, ctx) => {
                    const phone = parsePhoneNumberFromString(arg, {
                      defaultCountry: "DE",
                      extract: false,
                    });

                    if (phone && phone.isValid()) {
                      return phone.number;
                    }

                    if (
                      (arg === "" || arg === null || arg === undefined) &&
                      !item.required
                    ) {
                      return arg;
                    }

                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: item.required ? "Erforderlich" : "Ungültige Telefonnummer",
                    });

                    return z.NEVER;
                  })
                : item.type === "number"
                  ? item.required
                    ? z.coerce.number({
                        invalid_type_error: "Ungültige Zahl",
                      }).min(0, { message: "Zahl muss positiv sein" })
                    : z.coerce.number({
                        invalid_type_error: "Ungültige Zahl",
                      }).min(0, { message: "Zahl muss positiv sein" }).optional()
                  : item.type === "checkbox"
                    ? item.required
                      ? z.literal(true, {
                          errorMap: () => ({
                            message: "Erforderlich",
                          }),
                        })
                      : z.boolean()
                    : z.unknown(),
      };
    }, {});
  }, [cleanFormData]);

  const formSchema = useMemo(() => {
    return z.object(formFields);
  }, [formFields]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: cleanFormData?.fields?.reduce((obj, item) => {
      return {
        ...obj,
        [item.name]: item.type === "checkbox" ? false : "",
      };
    }, {}),
  });

  async function onSubmit(fieldData: any) {
    if (cleanFormData && fieldData) {
      const affiliateCode = forwardAffiliateToWebhook
        ? new URLSearchParams(window.location.search).get("aff")
        : null;
      const submissionData = affiliateCode
        ? { ...fieldData, affiliate_code: affiliateCode }
        : fieldData;

      setLoading(true);
      if (fieldData.email) {
        postHog.identify(fieldData.email);
      }
      postHog.capture("form_submit", {
        form: cleanFormData._id,
        formTitle: cleanFormData.name,
        fields: submissionData,
      });
      const res = await submitFormDataToZapier(submissionData, cleanFormData);
      if (res?.success === true) {
        sendGTMEvent({
          event: "zapierFormSubmit",
          "zapier-form-url": cleanFormData?.settings?.url ?? null,
          pageName: document.title,
          pagePath: window.location.href,
          // @ts-ignore
          "form-firstname": formData?.firstName ?? formData?.name,
          // @ts-ignore
          "form-email": formData?.email,
        });

        if (cleanFormData?.settings?.onSubmit === "message") {
          setSuccessMessage(
            cleanFormData?.settings?.message ?? "Form submitted successfully"
          );
          setLoading(false);
        } else if (cleanFormData?.settings?.onSubmit === "redirect") {
          const redirectUrl = resolvePageLink(
            cleanFormData?.settings?.redirect ?? null
          );
          if (redirectUrl) {
            router.push(redirectUrl);
          } else {
            setSuccessMessage(
              `Form submitted successfully. Could not redirect to page ${redirectUrl}`
            );
          }
        }
      } else {
        setLoading(false);
        setErrorMessage("Form submission failed. Try again later.");
      }
    }
  }

  return loading ? (
    <div className="flex h-[20rem] w-full items-center justify-center">
      <Loader className="h-[8rem] w-[8rem] text-gray-400" />
    </div>
  ) : (
    <>
      {cleanFormData?.fields ? (
        <Form {...form}>
          <form
            className={cn(
              "w-full flex flex-col justify-start items-stretch relative max-w-full",
              isNewTheme ? "gap-[1rem]" : "gap-[2rem]"
            )}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {cleanFormData?.fields?.map((formField, index) => {
              return (
                <FormField
                  key={index}
                  control={form.control}
                  // @ts-ignore - this is a valid prop
                  name={formField.name}
                  disabled={loading}
                  render={({ field }) => (
                    <FormItem
                      className={
                        formField.type === "checkbox"
                          ? "flex items-start space-x-3"
                          : ""
                      }
                    >
                      {formField.type === "checkbox" ? (
                        <>
                          <FormControl>
                            <input
                              type="checkbox"
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              className="flex-shrink-0 w-[2rem] h-[2rem] translate-y-[.125rem]"
                            />
                          </FormControl>
                          <div className="!mt-0 !ml-[1rem]">
                            <FormLabel className="text-[1.5rem] font-normal">
                              <p className="mb-0">{formField.label}</p>
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </>
                      ) : (
                        <>
                          {/* <FormLabel>{formField.label}</FormLabel> */}
                          <FormControl>
                            <Input
                              type={formField.type === "number" ? "number" : formField.type}
                              min={formField.type === "number" ? 0 : undefined}
                              placeholder={formField.placeholder}
                              theme={isNewTheme ? "new" : "default"}
                              {...field}
                              value={undefined}
                              className="!py-[1.5rem]"
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 text-sm" />
                        </>
                      )}
                    </FormItem>
                  )}
                />
              );
            })}
            <div className={cn(
              "relative z-10",
              isNewTheme ? "w-full" : "lg:px-[10rem] mt-6"
            )}>
              {isNewTheme ? (
                <CornerButton
                  htmlType="submit"
                  disabled={loading}
                  className="w-full [clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)]"
                >
                  <div className="corner-button-text relative">
                    <div className="corner-button-text-initial transition-all duration-300">
                      {formData?.settings?.submitText ?? "Submit"}
                    </div>
                    <div className="corner-button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
                      {formData?.settings?.submitText ?? "Submit"}
                    </div>
                  </div>

                  <div className="corner-button-divider h-[4rem]" />

                  <div className="corner-button-icon relative [&_svg]:w-full overflow-clip">
                    <div className="corner-button-icon-initial transition-all duration-300 w-[3rem] h-[3rem]">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="2 8.5 20.3 23">
                        <path fill="currentColor" d="M2.34998 20h2.8v2.8h-2.8zM2.34998 22.8h2.8v2.8h-2.8zM2.34998 25.6h2.8v2.8h-2.8z"/>
                        <path fill="currentColor" d="M2.34998 28.4h2.8v2.8h-2.8zM5.15015 17.2001h2.8v2.8h-2.8zM7.95007 14.4001h2.8v2.8h-2.8z"/>
                        <path fill="currentColor" d="M10.75 14.4001h2.8v2.8h-2.8zM13.55 8.80005h2.8v2.8h-2.8z"/>
                        <path fill="currentColor" d="M13.5499 14.4001h2.8v2.8h-2.8zM13.5499 20.0001h2.8v2.8h-2.8zM16.3499 11.6h2.8v2.8h-2.8zM16.3499 17.2001h2.8v2.8h-2.8zM19.1501 14.4001h2.8v2.8h-2.8z"/>
                      </svg>
                    </div>
                    <div className="corner-button-icon-hover absolute top-[2.25rem] -left-[2.25rem] transition-all duration-300 pointer-events-none w-[3rem] h-[3rem]">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="2 8.5 20.3 23">
                        <path fill="currentColor" d="M2.34998 20h2.8v2.8h-2.8zM2.34998 22.8h2.8v2.8h-2.8zM2.34998 25.6h2.8v2.8h-2.8z"/>
                        <path fill="currentColor" d="M2.34998 28.4h2.8v2.8h-2.8zM5.15015 17.2001h2.8v2.8h-2.8zM7.95007 14.4001h2.8v2.8h-2.8z"/>
                        <path fill="currentColor" d="M10.75 14.4001h2.8v2.8h-2.8zM13.55 8.80005h2.8v2.8h-2.8z"/>
                        <path fill="currentColor" d="M13.5499 14.4001h2.8v2.8h-2.8zM13.5499 20.0001h2.8v2.8h-2.8zM16.3499 11.6h2.8v2.8h-2.8zM16.3499 17.2001h2.8v2.8h-2.8zM19.1501 14.4001h2.8v2.8h-2.8z"/>
                      </svg>
                    </div>
                  </div>
                </CornerButton>
              ) : (
                <Button
                  type="submit"
                  disabled={loading}
                  className="relative w-full group overflow-clip"
                  variant="default"
                  size="default"
                  shape="pill"
                  action={"none"}
                >
                  <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 group-hover:-translate-y-full button-bg" />
                  <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-bg-hover" />
                  <div className="relative transition-all duration-200 button-text overflow-clip">
                    {formData?.settings?.submitText ?? "Submit"}
                  </div>
                </Button>
              )}
            </div>
            {successMessage && (
              <div className="w-full bg-green-100 border-green-500 border rounded-sm font-bold text-green-900 py-[1rem] px-[2rem] drop-shadow-md">
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className="w-full bg-red-100 border-red-500 border rounded-sm font-bold text-red-900 py-[1rem] px-[2rem] drop-shadow-md">
                {errorMessage}
              </div>
            )}
          </form>
        </Form>
      ) : (
        <div className="bg-gray-100 border-black border-2 rounded-xl font-bold text-blue-900 p-20">
          MISSING FORM DATA
        </div>
      )}
    </>
  );
}
